import { useEffect, useRef, useState } from "preact/hooks";
import "./DestructiveActionButton.css";

export function DestructiveActionButton({
  children = "Delete",
  className = "",
  disabled = false,
  onArmedChange = null,
  onConfirm = null,
  timeoutMs = 3200,
  type = "button",
  ...props
}) {
  const [armed, setArmed] = useState(false);
  const [pending, setPending] = useState(false);
  const timeoutRef = useRef(0);

  useEffect(() => {
    onArmedChange?.(armed);
  }, [armed, onArmedChange]);

  useEffect(() => () => {
    window.clearTimeout(timeoutRef.current);
  }, []);

  function clearArmState() {
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = 0;
    setArmed(false);
  }

  function armButton() {
    window.clearTimeout(timeoutRef.current);
    setArmed(true);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = 0;
      setArmed(false);
    }, timeoutMs);
  }

  async function handleClick() {
    if (disabled || pending) {
      return;
    }

    if (!armed) {
      armButton();
      return;
    }

    try {
      clearArmState();
      setPending(true);
      await onConfirm?.();
    } finally {
      setPending(false);
    }
  }

  function handleBlur() {
    clearArmState();
  }

  function handleKeyDown(event) {
    if (event.key !== "Escape") {
      return;
    }

    clearArmState();
  }

  const buttonClassName = [
    "destructive-action-button",
    armed ? "destructive-action-button--armed" : "",
    pending ? "destructive-action-button--pending" : "",
    className
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      {...props}
      class={buttonClassName}
      disabled={disabled || pending}
      onBlur={handleBlur}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{ "--destructive-action-timeout": `${timeoutMs}ms` }}
      type={type}
    >
      <span class="destructive-action-button__label">{children}</span>
    </button>
  );
}
